/**
 * @file Backend-mediated Compliance evidence modal and shared attachment UI adapter.
 */

import {
    canPreviewAttachmentFile,
    normalizeAttachmentRecords,
    isOfficeAttachmentFile,
} from "../../shared/attachments/attachmentModel.js";
import {openAttachmentPreview} from "../../shared/attachments/attachmentPreview.js";
import {
    attachAttachmentsSectionListeners,
    renderAttachmentsSection,
} from "../../shared/details/attachmentsSection.js";
import {closeForm} from "../../shared/forms/modalShell.js";
import {openStandardForm} from "../../shared/forms/standardFormModal.js";
import {openConfirmationModal} from "../../shared/ui/confirmationModal.js";
import {escapeHTML} from "../../shared/utils/contentUtils.js";
import {
    createComplianceEvidenceUpload,
    deleteComplianceEvidence,
    finalizeComplianceEvidenceUpload,
    getComplianceEvidenceOwner,
    getComplianceEvidenceReadUrl,
    updateComplianceEvidenceMetadata,
} from "./complianceApi.js";

function buildRecordOwner(item, requirement, record) {
    return {
        ownerType: record.sourceType === "maintenance_service" ?
            "derived_service" :
            "record",
        itemId: item.id,
        requirementId: requirement.id,
        ownerId: record.id,
    };
}

function showEvidenceFailure(error) {
    openConfirmationModal(
        "Evidence unavailable",
        escapeHTML(error?.message ||
            "The Compliance evidence action could not be completed."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

async function uploadEvidenceFile(owner, file, attachmentId = null) {
    const authorization = await createComplianceEvidenceUpload({
        owner,
        attachmentId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || "application/octet-stream",
    });
    const upload = await fetch(authorization.uploadUrl, {
        method: "PUT",
        headers: authorization.requiredHeaders,
        body: file,
    });
    if (!upload.ok) {
        throw new Error(`Evidence upload failed (${upload.status}).`);
    }
    return finalizeComplianceEvidenceUpload({
        owner,
        attachmentId: authorization.attachmentId,
        versionId: authorization.versionId,
        fileName: authorization.fileName,
        fileSize: authorization.fileSize,
        fileType: authorization.fileType,
        uploadIntent: authorization.uploadIntent,
    });
}

function openEvidenceDownloadPreparationWindow() {
    const preparationWindow = window.open("", "_blank");
    if (!preparationWindow) {
        showEvidenceFailure(new Error(
            "Allow pop-ups to prepare the selected evidence downloads.",
        ));
        return null;
    }
    preparationWindow.opener = null;
    preparationWindow.document.open();
    preparationWindow.document.write(`<!doctype html>
        <html><head><meta charset="utf-8">
        <title>Preparing evidence downloads</title>
        <style>
            body{font:15px Arial,sans-serif;color:#111;margin:24px;line-height:1.5}
            h1{font-size:20px;margin:0 0 8px}
            ul{padding-left:20px} a{color:#075985}
        </style></head><body>
        <h1>Preparing evidence downloads</h1>
        <p>Please keep this window open.</p>
        </body></html>`);
    preparationWindow.document.close();
    return preparationWindow;
}

function renderEvidenceDownloadLinks(preparationWindow, links) {
    const items = links.map(({fileName, readUrl}) => `
        <li><a href="${escapeHTML(readUrl)}" target="_blank"
            rel="noopener">${escapeHTML(fileName || "Evidence")}</a></li>
    `).join("");
    preparationWindow.document.open();
    preparationWindow.document.write(`<!doctype html>
        <html><head><meta charset="utf-8">
        <title>Evidence downloads ready</title>
        <style>
            body{font:15px Arial,sans-serif;color:#111;margin:24px;line-height:1.5}
            h1{font-size:20px;margin:0 0 8px}
            ul{padding-left:20px} a{color:#075985}
        </style></head><body>
        <h1>Evidence downloads ready</h1>
        <p>These links are short-lived. Open each file you need.</p>
        <ul>${items}</ul>
        </body></html>`);
    preparationWindow.document.close();
}

async function authorizeRead(owner, attachment, version) {
    const response = await getComplianceEvidenceReadUrl({
        owner,
        attachmentId: attachment.id,
        versionId: version.id,
    });
    return {
        ...attachment,
        currentVersionId: version.id,
        currentVersion: {...version, url: response.readUrl},
    };
}

/**
 * Opens shared attachment UI for one manual Compliance record.
 * @param {object} options Modal context.
 * @param {object} options.item Tracked item.
 * @param {object} options.requirement Requirement.
 * @param {object} options.record Manual or projected derived-Service record.
 * @param {Function} [options.onChanged] Reconciliation callback.
 * @returns {Promise<void>}
 */
export async function openComplianceEvidenceModal({
    item,
    requirement,
    record,
    onChanged,
}) {
    const owner = buildRecordOwner(item, requirement, record);
    let attachments = [];
    let cleanupPending = false;
    try {
        const response = await getComplianceEvidenceOwner({owner});
        attachments = normalizeAttachmentRecords(response.owner.attachments);
        cleanupPending = response.owner.cleanupPending === true;
    } catch (error) {
        showEvidenceFailure(error);
        return;
    }

    const render = () => {
        const root = document.querySelector("[data-compliance-evidence-root]");
        if (!root) {return;}
        const itemModel = {
            id: `compliance-${record.id}`,
            attachments,
        };
        const pendingNotice = cleanupPending ? `
            <div class="mb-3 rounded-md border border-amber-300 bg-amber-50
                px-3 py-2 text-sm text-amber-900"
                data-compliance-evidence-cleanup-pending>
                Evidence file cleanup is pending. Retry the same delete action.
            </div>
        ` : "";
        root.innerHTML = pendingNotice + renderAttachmentsSection(itemModel)
            .replace(">Attachments<", ">Evidence<");

        const acceptOwner = (response) => {
            attachments = normalizeAttachmentRecords(
                response.owner.attachments,
            );
            cleanupPending = response.owner.cleanupPending === true;
            onChanged?.(attachments.length);
            render();
        };
        const runEvidenceAction = async (action) => {
            try {
                return await action();
            } catch (error) {
                showEvidenceFailure(error);
                return null;
            }
        };
        const uploadFiles = async (event) => {
            const files = [...(event.target?.files || [])];
            event.target.value = "";
            try {
                for (const file of files) {
                    acceptOwner(await uploadEvidenceFile(owner, file));
                }
            } catch (error) {
                showEvidenceFailure(error);
            }
        };
        attachAttachmentsSectionListeners(root, itemModel, {
            handleAttachmentBulkDelete: async (selected) => {
                const response = await runEvidenceAction(() =>
                    deleteComplianceEvidence({
                        owner,
                        attachmentIds: selected.map(({id}) => id),
                    }));
                if (response) {acceptOwner(response);}
            },
            handleAttachmentBulkDownload: async (ids) => {
                const preparationWindow =
                    openEvidenceDownloadPreparationWindow();
                if (!preparationWindow) {return;}
                await runEvidenceAction(async () => {
                    const links = [];
                    for (const id of ids) {
                        const attachment = attachments.find((entry) =>
                            entry.id === id);
                        if (!attachment) {continue;}
                        const response = await getComplianceEvidenceReadUrl({
                            owner,
                            attachmentId: attachment.id,
                            versionId: attachment.currentVersion.id,
                        });
                        links.push({
                            fileName: response.fileName ||
                                attachment.displayName,
                            readUrl: response.readUrl,
                        });
                    }
                    renderEvidenceDownloadLinks(preparationWindow, links);
                });
            },
            handleAttachmentLinkCopy: async () => null,
            handleAttachmentMetadataSave: async (metadata) => {
                acceptOwner(await updateComplianceEvidenceMetadata({
                    owner,
                    attachmentId: metadata.attachmentId,
                    displayName: metadata.displayName,
                    description: metadata.description,
                }));
            },
            handleAttachmentOfficePreview: async () => {
                throw new Error("Open this Office evidence file directly.");
            },
            handleAttachmentVersionUpload: async ({attachment, file}) => {
                const response = await runEvidenceAction(() =>
                    uploadEvidenceFile(owner, file, attachment.id));
                if (response) {acceptOwner(response);}
            },
            handleFileUpload: uploadFiles,
            handleFileDelete: async (filePath, version, options = {}) => {
                const attachment = attachments.find((entry) =>
                    entry.currentVersion?.path === filePath ||
                    entry.versions.some(({path}) => path === filePath));
                if (!attachment) {return;}
                const response = await runEvidenceAction(() =>
                    deleteComplianceEvidence({
                        owner,
                        attachmentId: attachment.id,
                        versionId: options.deleteMode === "version" ?
                            version.id :
                            null,
                    }));
                if (response) {acceptOwner(response);}
            },
        });

        root.querySelectorAll(".attachment-link-btn").forEach((button) => {
            button.remove();
        });
        const bindRead = (button, attachment, version) => {
            button.onclick = async (event) => {
                event.preventDefault();
                const needsPreparationWindow =
                    isOfficeAttachmentFile(version) ||
                    !canPreviewAttachmentFile(version);
                const preparationWindow = needsPreparationWindow ?
                    openEvidenceDownloadPreparationWindow() :
                    null;
                if (needsPreparationWindow && !preparationWindow) {return;}
                try {
                    const authorized = await authorizeRead(
                        owner,
                        attachment,
                        version,
                    );
                    if (preparationWindow) {
                        preparationWindow.location.replace(
                            authorized.currentVersion.url,
                        );
                        return;
                    }
                    await openAttachmentPreview({attachment: authorized});
                } catch (error) {
                    preparationWindow?.close();
                    showEvidenceFailure(error);
                }
            };
        };
        root.querySelectorAll(".attachment-row").forEach((row) => {
            const attachment = attachments.find(({id}) =>
                id === row.dataset.attachmentId);
            if (!attachment) {return;}
            const currentDownload = row.querySelector(
                ":scope > div > div:last-child a",
            );
            if (currentDownload) {
                currentDownload.href = "#";
                bindRead(
                    currentDownload,
                    attachment,
                    attachment.currentVersion,
                );
            }
            const openButton = row.querySelector(".attachment-open-btn");
            if (openButton) {
                bindRead(
                    openButton,
                    attachment,
                    attachment.currentVersion,
                );
            }
            row.querySelectorAll(".attachment-version-row")
                .forEach((versionRow) => {
                    const version = attachment.versions.find(({id}) =>
                        id === versionRow.dataset.versionId);
                    if (!version) {return;}
                    const link = versionRow.querySelector("a");
                    if (link) {
                        link.href = "#";
                        bindRead(link, attachment, version);
                    }
                    const button = versionRow.querySelector(
                        ".attachment-version-open-btn",
                    );
                    if (button) {
                        bindRead(button, attachment, version);
                    }
                });
        });
    };

    openStandardForm("compliance_evidence", {id: record.id}, {
        getFieldsHtml: () => `
            <div data-compliance-evidence-root>
                <p class="text-sm text-gray-500">Loading evidence...</p>
            </div>
        `,
        afterRender({itemForm}) {
            document.getElementById("form-title").textContent =
                `Evidence — ${record.startDate || requirement.name}`;
            itemForm.querySelector("button[type=submit]")?.remove();
            const cancel = document.getElementById("cancel-form-btn");
            if (cancel) {
                cancel.textContent = "Close";
                cancel.onclick = () => closeForm();
            }
            itemForm.onsubmit = (event) => event.preventDefault();
            render();
        },
    });
}
